import { AsyncStateNotification } from '@ui/notifications';
import { ButtonProps } from 'antd';
import { coreActions } from '@sonar-software/sonar-core';
import { createAsyncSelectors } from '@lib/selectors';
import { getFileExtensionForMimeType } from '@sonar-software/sonar-ts-lib';
import { MimeType } from '@sonar-software/sonar-ts-types';
import { SonarModal } from '@ui/modals';

import { COPY_GENERIC, COPY_SPECIFIC } from '@sonar-software/sonar-ts-constants';
import { FileDropArea, ImageFileCrop, ImageFileCropRef } from '@ui/files';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAction, useCurrentUser, useSonarSelector } from '@hooks';

const acceptedTypes = {
	'image/jpeg': [],
	'image/png': []
};

const cancelButtonProps: Partial<ButtonProps> = {
	danger: true,
	type: 'link'
};

export interface AvatarUploadModalProps {
	onAfterSave?: () => void;
	onCancel?: () => void;
	open: boolean;
}

const uploadSelectors = createAsyncSelectors((state) => state.core.users.current.picture.put);

export const AvatarUploadModal: React.FC<AvatarUploadModalProps> = ({ onAfterSave, ...rest }) => {
	const { id } = useCurrentUser();
	const [ file, setFile ] = useState<File>();

	const isProcessing = useSonarSelector(uploadSelectors.isRequested);
	const isFinished = !useSonarSelector(uploadSelectors.isIdle) && !isProcessing;

	const uploadProfilePicture = useAction(coreActions.requestPutCoreUsersCurrentPicture);
	const updateUserReference = useAction(coreActions.updateUserReferences);

	const imageCropRef = useRef<ImageFileCropRef>(null);

	useEffect(() => {
		if (!rest.open && file) {
			setFile(undefined);
		}
	}, [ file, rest.open ]);

	useEffect(() => {
		if (isFinished && onAfterSave) {
			onAfterSave();
		}
	}, [ isFinished, onAfterSave ]);

	const handleFileSelection = useCallback((files: Array<File>) => {
		if (!files.length) {
			return;
		}

		setFile(files[ 0 ]);
	}, []);

	const handleSave = useCallback(async() => {
		if (!imageCropRef.current) {
			return;
		}

		const croppedImage = await imageCropRef.current.getCroppedImage();

		if (croppedImage) {
			const mimeType = croppedImage.type as MimeType;
			const extension = getFileExtensionForMimeType(mimeType);
			const croppedFile = new File([ croppedImage ], `profile-picture-${id}${extension}`, {
				type: mimeType
			});

			uploadProfilePicture({
				file: croppedFile
			});

			updateUserReference([
				{
					profilePicture: URL.createObjectURL(croppedFile),
					profilePictureTimestamp: Date.now(),
					userId: id
				}
			]);
		}
	}, [ id, updateUserReference, uploadProfilePicture ]);

	return (
		<>
			<SonarModal
				fixedWidth
				{ ...rest }
				cancelButtonProps={ {
					...cancelButtonProps,
					disabled: isProcessing
				} }
				destroyOnHidden
				footer={ file ? undefined : null }
				okButtonProps={ {
					disabled: isProcessing
				} }
				okText={ COPY_SPECIFIC.SAVE }
				onOk={ handleSave }
				title={ COPY_SPECIFIC.UPLOAD_YOUR_PHOTO }>
				<section className='avatar-crop-container'>
					{ !file &&
						<FileDropArea
							accept={ acceptedTypes }
							onAccept={ handleFileSelection }
							unsupportedMessage={ COPY_SPECIFIC.UNSUPPORTED_FILE_TYPE_IMAGE } /> }
					{ file && <ImageFileCrop
						cropShape='round'
						ref={ imageCropRef }
						sourceFile={ file } /> }
				</section>
			</SonarModal>
			<AsyncStateNotification
				errored={ COPY_GENERIC.CONTACT_SUPPORT }
				failed={ COPY_GENERIC.CONTACT_SUPPORT }
				resetRawAction={ coreActions.resetPutCoreUsersCurrentPicture }
				stateSelector={ uploadSelectors.state } />
		</>
	);
};
